import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import { useGetMyOrganisations } from '@api/my-organisation/my-organisation'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'

interface ActiveOrganisation {
  /** Every organisation the user belongs to (undefined while loading or on error). */
  organisations: MyOrganisationDTO[] | undefined
  /** The organisation the user is working in (undefined while loading or on error). */
  activeOrganisation: MyOrganisationDTO | undefined
}

interface Options {
  /**
   * Show a snackbar when loading the organisations fails. Off for purely informational
   * consumers (the navbar's organisation name): the Home page's project table and the account
   * menu's switcher report a failed load of the same request, so the editor doesn't nag about
   * it on every load.
   */
  notifyOnError?: boolean
}

/**
 * The user's organisations and the one they are currently working in. Shared by the account
 * menu's organisation switcher and the project name in the navbar.
 */
const useActiveOrganisation = ({ notifyOnError = true }: Options = {}): ActiveOrganisation => {
  const { enqueueSnackbar } = useSnackbar()
  const activeOrganisationGuid = useAppStore(state => state.activeOrganisationGuid)

  const { data: organisations } = useGetMyOrganisations<MyOrganisationDTO[], AxiosError>({
    query: {
      select: data => data.organisations,
      staleTime: Infinity,
      retry: false,
      onError: error => {
        if (!notifyOnError) return
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Organisationen'), {
          variant: 'error',
          // Every consumer observes the same request, so one failure would otherwise show twice.
          preventDuplicate: true,
        })
      },
    },
  })

  // Without a choice the backend answers for the default organisation, which
  // is the first one listed.
  const activeOrganisation =
    organisations?.find(organisation => organisation.guid === activeOrganisationGuid) ??
    organisations?.[0]

  return { organisations, activeOrganisation }
}

export default useActiveOrganisation
